import React, { Component } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import type {
  DashboardFilterOptions,
  DashboardPageExport,
  DashboardPageRefresh,
  DashboardSharedFilters,
} from "./dashboard.types";

export interface DashboardHeaderProps {
  userData: any;
  sharedFilters?: DashboardSharedFilters;
  filterOptions?: DashboardFilterOptions;
  onFilterChange?: (patch: Partial<DashboardSharedFilters>) => void;
  title?: string;
  subtitle?: string;
  // Compatibility props for legacy, unregistered drill components.
  period?: string;
  scenario?: string;
  costcenter?: string;
  location?: string;
  onRefresh?: DashboardPageRefresh;
  onExport?: DashboardPageExport;
}

export default class DashboardHeader extends Component<DashboardHeaderProps> {
  handleDateChange =
    (field: "datefrom" | "dateto") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      this.props.onFilterChange?.({ [field]: event.target.value });
    };

  handleIdChange =
    (field: "locationid" | "salespersonid") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      this.props.onFilterChange?.({
        [field]: value === "" ? null : Number(value),
      });
    };

  render() {
    const {
      title,
      subtitle,
      sharedFilters,
      onRefresh,
      onExport,
    } = this.props;

    const {
      datefrom,
      dateto,
      locationid,
      salespersonid,
    } = sharedFilters || {};

    return (
      <Box>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 28, md: 34 },
                fontWeight: 800,
                lineHeight: 1.15,
                color: "#17324D",
                letterSpacing: "-0.03em",
              }}
            >
              {title}
            </Typography>

            {subtitle ? (
              <Typography
                sx={{ mt: 0.75, fontSize: 14, color: "#66788A", lineHeight: 1.7 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Button
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadOutlinedIcon />}
              onClick={onExport}
            >
              Export
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          sx={{ mt: 2.5 }}
        >
          <TextField
            label="Date from"
            type="date"
            value={datefrom || ""}
            onChange={this.handleDateChange("datefrom")}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="Date to"
            type="date"
            value={dateto || ""}
            onChange={this.handleDateChange("dateto")}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="Location ID"
            type="number"
            value={locationid ?? ""}
            onChange={this.handleIdChange("locationid")}
            helperText="Blank includes all locations"
            inputProps={{ min: 1 }}
            sx={{ minWidth: 190 }}
          />
          <TextField
            label="Salesperson ID"
            type="number"
            value={salespersonid ?? ""}
            onChange={this.handleIdChange("salespersonid")}
            helperText="Blank includes all salespeople"
            inputProps={{ min: 1 }}
            sx={{ minWidth: 205 }}
          />
        </Stack>
      </Box>
    );
  }
}
